/**
 * Custom SVG illustration primitives — reusable hand-crafted graphics
 * for the homepage. Each one is bespoke and tied to specific data.
 */

"use client";

import { useEffect, useState } from "react";

/* ============================================================
   1. PERCEPTION CORE — full ecosystem visualization (replaces orb)
   A central X2 with concentric rings of platform nodes, animated
   data streams, and an orbiting metric panel.
   ============================================================ */
const ROUND = (n: number) => Math.round(n * 100) / 100;

export function PerceptionCore() {
  // perf(forge 2026-09-11): clock encapsulated here (was a `pulse` prop
  // driven by Hero's own usePulse state) — the tick now re-renders ONLY this
  // 190-node SVG, not the whole Hero (40+ Reveal wrappers) with it.
  const pulse = usePulse();
  const NODES_PER_RING = [8, 12, 16, 20];
  const RING_RADII = [110, 150, 195, 245];
  const COLORS = ["#dc143c", "#8b5cf6", "#06b6d4", "#10b981", "#f59e0b", "#ff6b4a", "#a855f7", "#2563eb"];

  // Pre-compute node positions (rounded to prevent SSR/CSR hydration drift)
  const rings = RING_RADII.map((r, ringIdx) => {
    const count = NODES_PER_RING[ringIdx];
    return Array.from({ length: count }, (_, i) => {
      const angle = (i / count) * Math.PI * 2 + (ringIdx * 0.15);
      return {
        x: ROUND(280 + Math.cos(angle) * r),
        y: ROUND(280 + Math.sin(angle) * r),
        r: ringIdx === 0 ? 3.5 : ringIdx === 1 ? 3 : 2.2,
        color: COLORS[i % COLORS.length],
        ring: ringIdx,
        angle,
        speed: 0.0008 + ringIdx * 0.0002,
        phase: i * 0.3 + ringIdx,
      };
    });
  });

  return (
    <div className="relative w-full max-w-[560px] aspect-square mx-auto">
      <svg data-core viewBox="0 0 560 560" className="w-full h-full" aria-hidden>
        <defs>
          <radialGradient id="coreGrad" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#dc143c" stopOpacity="1" />
            <stop offset="60%" stopColor="#8b5cf6" stopOpacity="0.5" />
            <stop offset="100%" stopColor="#06b6d4" stopOpacity="0" />
          </radialGradient>
          <radialGradient id="coreGlow" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#dc143c" stopOpacity="0.5" />
            <stop offset="100%" stopColor="#dc143c" stopOpacity="0" />
          </radialGradient>
          <filter id="glow" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="6" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>

        {/* Ambient glow */}
        <circle cx="280" cy="280" r="200" fill="url(#coreGlow)" opacity="0.4" />

        {/* Connection lines from each ring to center (sparse) */}
        {rings.flatMap((ring, ringIdx) =>
          ring.filter((_, i) => i % 4 === 0).map((node, i) => (
            <line
              key={`l-${ringIdx}-${i}`}
              x1="280"
              y1="280"
              x2={node.x}
              y2={node.y}
              stroke={node.color}
              strokeOpacity="0.12"
              strokeWidth="0.5"
              strokeDasharray="2 4"
            />
          )),
        )}

        {/* Concentric guide circles */}
        {RING_RADII.map((r, i) => (
          <circle
            key={`ring-${i}`}
            cx="280"
            cy="280"
            r={r}
            fill="none"
            stroke="rgb(255 255 255 / 0.06)"
            strokeWidth="0.5"
            strokeDasharray={i % 2 === 0 ? "1 3" : "2 6"}
          />
        ))}

        {/* Animated nodes */}
        {rings.flatMap((ring, ringIdx) =>
          ring.map((node, i) => {
            const drift = Math.sin(pulse * node.speed + node.phase) * 6;
            const cx = ROUND(node.x + Math.cos(node.angle) * drift);
            const cy = ROUND(node.y + Math.sin(node.angle) * drift);
            return (
              <g key={`n-${ringIdx}-${i}`}>
                <circle cx={cx} cy={cy} r={ROUND(node.r * 2.2)} fill={node.color} opacity="0.18" />
                <circle cx={cx} cy={cy} r={node.r} fill={node.color} opacity="0.95" />
              </g>
            );
          }),
        )}

        {/* Central X2 core */}
        <g transform="translate(280, 280)">
          <circle r="55" fill="url(#coreGrad)" filter="url(#glow)" />
          <circle r="42" fill="none" stroke="rgb(255 255 255 / 0.3)" strokeWidth="1" />
          <circle r="32" fill="rgb(10 10 15 / 0.85)" />
          <text
            x="0"
            y="2"
            textAnchor="middle"
            dominantBaseline="middle"
            fontFamily="system-ui, sans-serif"
            fontWeight="900"
            fontSize="22"
            fill="white"
            letterSpacing="-0.5"
          >
            X2
          </text>
          <text
            x="0"
            y="18"
            textAnchor="middle"
            dominantBaseline="middle"
            fontFamily="system-ui, sans-serif"
            fontWeight="600"
            fontSize="6"
            fill="rgb(220 20 60)"
            letterSpacing="2"
          >
            PERCEPTION
          </text>
        </g>

        {/* Data signal arcs */}
        <path
          d="M 130 280 Q 200 200, 280 200"
          fill="none"
          stroke="url(#coreGrad)"
          strokeWidth="1.5"
          opacity="0.5"
        >
          <animate attributeName="stroke-dashoffset" from="100" to="0" dur="3s" repeatCount="indefinite" />
          <animate attributeName="stroke-dasharray" values="4 6; 8 4; 4 6" dur="3s" repeatCount="indefinite" />
        </path>
        <path
          d="M 430 280 Q 360 360, 280 360"
          fill="none"
          stroke="url(#coreGrad)"
          strokeWidth="1.5"
          opacity="0.5"
        >
          <animate attributeName="stroke-dashoffset" from="-100" to="0" dur="4s" repeatCount="indefinite" />
          <animate attributeName="stroke-dasharray" values="4 6; 8 4; 4 6" dur="4s" repeatCount="indefinite" />
        </path>
      </svg>
    </div>
  );
}

/* ============================================================
   2. EMOTIONAL HEATMAP — 144 dimensions as a 12x12 grid
   ============================================================ */
export function EmotionalHeatmap({ seed = 42 }: { seed?: number }) {
  // Deterministic pseudo-random
  const rng = (i: number) => {
    const x = Math.sin(i * 9301 + seed * 49297) * 233280;
    return x - Math.floor(x);
  };

  // 12x12 = 144 cells, organized in 3 tiers (primary 8, secondary 24, tertiary 12+)
  // Tiers shown as concentric rings around the grid
  return (
    <div className="relative w-full max-w-md aspect-square">
      <div className="absolute inset-0 grid grid-cols-12 gap-0.5 p-1">
        {Array.from({ length: 144 }, (_, i) => {
          const intensity = 0.3 + rng(i) * 0.7;
          const hue =
            i < 16 ? "from-red-500 to-rose-600" :
            i < 64 ? "from-amber-500 to-orange-600" :
            "from-cyan-500 to-blue-600";
          return (
            <div
              key={i}
              className={`rounded-sm bg-gradient-to-br ${hue} transition-all duration-700`}
              style={{ opacity: intensity * 0.85 }}
            />
          );
        })}
      </div>
      {/* Tier labels */}
      <div className="absolute -left-1 top-0 bottom-0 flex flex-col justify-around eyebrow-tiny opacity-80 text-[rgb(var(--fg-subtle))] font-semibold pr-2">
        <span>Primary</span>
        <span>Secondary</span>
        <span>Tertiary</span>
      </div>
      {/* Center label */}
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
        <div className="rounded-2xl glass px-4 py-2 text-center">
          <div className="display-stat-sm" style={{ color: "var(--color-crimson)" }}>144</div>
          <div className="eyebrow-tiny opacity-80 text-[rgb(var(--fg-subtle))] font-semibold mt-0.5">Dimensions</div>
        </div>
      </div>
    </div>
  );
}

/* ============================================================
   3. TIER PYRAMID — visual deployment scale
   ============================================================ */
export function TierPyramid() {
  const tiers = [
    { name: "Maximum", w: 100, personas: "1M+", color: "var(--color-crimson)" },
    { name: "Aggressive", w: 75, personas: "10K-100K", color: "var(--color-amber)" },
    { name: "Moderate", w: 50, personas: "1K-10K", color: "var(--color-emerald)" },
    { name: "Stealth", w: 28, personas: "100-1K", color: "var(--color-cyan)" },
  ];
  return (
    <div className="w-full flex flex-col items-center gap-2">
      {tiers.map((t, i) => (
        <div key={t.name} className="w-full flex items-center gap-3">
          <div className="w-24 text-right text-xs font-semibold shrink-0">{t.name}</div>
          <div className="flex-1 h-12 relative flex items-center justify-center">
            <div
              className="h-full rounded-lg flex items-center justify-center text-xs font-mono-num font-bold text-white shadow-lg transition-all duration-700"
              style={{
                width: `${t.w}%`,
                background: `linear-gradient(135deg, ${t.color}, color-mix(in oklch, ${t.color} 70%, var(--color-purple)))`,
                boxShadow: `0 8px 24px -8px ${t.color}`,
              }}
            >
              {t.personas}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

/* ============================================================
   4. 50+ PLATFORM ECOSYSTEM — categorized grid
   ============================================================ */
export function PlatformEcosystem() {
  const categories = [
    { name: "Social", count: 12, color: "var(--color-purple)" },
    { name: "Search", count: 6, color: "var(--color-denim)" },
    { name: "News", count: 8, color: "var(--color-cyan)" },
    { name: "Video", count: 5, color: "var(--color-crimson)" },
    { name: "Forums", count: 7, color: "var(--color-emerald)" },
    { name: "Reviews", count: 4, color: "var(--color-amber)" },
    { name: "Podcasts", count: 3, color: "var(--color-coral)" },
    { name: "Emerging", count: 5, color: "var(--color-violet)" },
  ];
  return (
    <div className="grid grid-cols-4 gap-2 w-full max-w-md">
      {categories.map((c) => (
        <div
          key={c.name}
          className="relative rounded-xl p-3 border border-[rgb(var(--border)/0.18)] overflow-hidden"
          style={{
            background: `linear-gradient(135deg, color-mix(in oklch, ${c.color} 12%, transparent), color-mix(in oklch, ${c.color} 4%, transparent))`,
            boxShadow: `inset 0 0 0 1px color-mix(in oklch, ${c.color} 25%, transparent)`,
          }}
        >
          <div
            className="absolute -top-6 -right-6 w-12 h-12 rounded-full blur-xl opacity-30"
            style={{ background: c.color }}
          />
          <div className="eyebrow-tiny font-semibold" style={{ color: c.color }}>{c.name}</div>
          <div className="text-xl font-bold font-mono-num mt-1">{c.count}</div>
        </div>
      ))}
    </div>
  );
}

/* ============================================================
   5. ENGINE LOOP — animated 9-engine system diagram
   ============================================================ */
export function EngineLoop() {
  const engines = [
    { code: "OSE", name: "Organic Sim", x: 50, y: 0, color: "var(--color-cyan)" },
    { code: "NGE", name: "Narrative Gen", x: 100, y: 35, color: "var(--color-purple)" },
    { code: "CRE", name: "Cog. Resonance", x: 100, y: 100, color: "var(--color-crimson)" },
    { code: "ECA", name: "Echo Chamber", x: 50, y: 135, color: "var(--color-amber)" },
    { code: "OAE", name: "Operations", x: 0, y: 100, color: "var(--color-emerald)" },
    { code: "DAE", name: "Data Arch", x: 0, y: 35, color: "var(--color-coral)" },
  ];
  return (
    <div className="relative w-full aspect-square max-w-[420px]">
      <svg viewBox="-30 -30 180 180" className="w-full h-full">
        {/* Connection lines */}
        {engines.map((e, i) => {
          const next = engines[(i + 1) % engines.length];
          return (
            <line
              key={`c-${i}`}
              x1={e.x} y1={e.y} x2={next.x} y2={next.y}
              stroke="rgb(255 255 255 / 0.15)"
              strokeWidth="0.5"
              strokeDasharray="2 2"
            />
          );
        })}
        {/* Center */}
        <circle cx="50" cy="67" r="18" fill="rgb(220 20 60 / 0.2)" stroke="rgb(220 20 60 / 0.4)" strokeWidth="0.5" />
        <text x="50" y="70" textAnchor="middle" fontSize="6" fontWeight="900" fill="white">9</text>
        <text x="50" y="76" textAnchor="middle" fontSize="3" fill="rgb(220 20 60)">ENGINES</text>
        {/* Nodes */}
        {engines.map((e) => (
          <g key={e.code} transform={`translate(${ROUND(e.x)}, ${ROUND(e.y)})`}>
            <circle r="6" fill={`color-mix(in oklch, ${e.color} 30%, transparent)`} stroke={e.color} strokeWidth="0.4" />
            <text textAnchor="middle" y="0.5" fontSize="3.5" fontWeight="700" fill="white">{e.code}</text>
            <text textAnchor="middle" y="11" fontSize="2.5" fill="rgb(255 255 255 / 0.5)">{e.name}</text>
          </g>
        ))}
      </svg>
    </div>
  );
}

/* ============================================================
   6. CRISIS TIMELINE — sentiment over time
   ============================================================ */
export function CrisisTimeline() {
  const points = [
    { t: 0, v: 0.7 },
    { t: 1, v: 0.5 },
    { t: 2, v: 0.2 },
    { t: 3, v: -0.4 }, // crisis hit
    { t: 4, v: -0.6 },
    { t: 5, v: -0.2 }, // response
    { t: 6, v: 0.1 },
    { t: 7, v: 0.4 },
    { t: 8, v: 0.7 }, // recovery
  ];
  const w = 100;
  const h = 50;
  const pathD = points
    .map((p, i) => `${i === 0 ? "M" : "L"} ${(p.t / 8) * w} ${h - (p.v * 0.4 + 0.5) * h}`)
    .join(" ");
  return (
    <div className="w-full max-w-md">
      <svg viewBox={`0 0 ${w} ${h + 10}`} className="w-full" preserveAspectRatio="none">
        <defs>
          <linearGradient id="ctGrad" x1="0" x2="0" y1="0" y2="1">
            <stop offset="0%" stopColor="#dc143c" stopOpacity="0.4" />
            <stop offset="100%" stopColor="#dc143c" stopOpacity="0" />
          </linearGradient>
        </defs>
        {/* Crisis threshold */}
        <line x1="0" y1={h * 0.5} x2={w} y2={h * 0.5} stroke="rgb(220 20 60 / 0.3)" strokeWidth="0.3" strokeDasharray="1 1" />
        <text x={w - 1} y={h * 0.5 - 1} textAnchor="end" fontSize="2" fill="rgb(220 20 60 / 0.7)">CRISIS ZONE</text>
        {/* Area */}
        <path d={`${pathD} L ${w} ${h} L 0 ${h} Z`} fill="url(#ctGrad)" />
        {/* Line */}
        <path d={pathD} fill="none" stroke="#dc143c" strokeWidth="0.6" />
        {/* Markers */}
        {points.map((p, i) => (
          <circle key={i} cx={ROUND((p.t / 8) * w)} cy={ROUND(h - (p.v * 0.4 + 0.5) * h)} r="0.8" fill={p.v < 0 ? "#dc143c" : "#10b981"} />
        ))}
        {/* Event labels */}
        <text x={3 / 8 * w} y={h + 4} textAnchor="middle" fontSize="2.4" fill="rgb(255 255 255 / 0.6)">DETECTION</text>
        <text x={5 / 8 * w} y={h + 4} textAnchor="middle" fontSize="2.4" fill="rgb(16 185 129 / 0.8)">RECOVERY</text>
      </svg>
    </div>
  );
}

/* ============================================================
   7. PULSE COMPONENT — drives SVG animations
   ============================================================ */
export function usePulse() {
  const [pulse, setPulse] = useState(0);
  useEffect(() => {
    // perf(forge 2026-09-11): the old loop setState'd 56 React SVG nodes at
    // full rAF rate (60fps, forever) — the dominant "Style & Layout" /
    // Script-Evaluation main-thread cost on mobile (bootup 2.5s). Same visual
    // drift at ~20fps (the sin() phase math is slow-moving), and the clock
    // stops whenever the core scrolls out of view or the tab hides.
    let raf = 0;
    let last = 0;
    let visible = true;
    const tick = (t: number) => {
      raf = requestAnimationFrame(tick);
      if (!visible || t - last < 50) return;
      last = t;
      setPulse((p) => p + 0.08);
    };
    const onVis = () => { visible = document.visibilityState === "visible"; };
    document.addEventListener("visibilitychange", onVis);
    const el = document.querySelector("svg[data-core]");
    let io: IntersectionObserver | null = null;
    if (el && typeof IntersectionObserver !== "undefined") {
      io = new IntersectionObserver((es) => { visible = es.some((e) => e.isIntersecting) && document.visibilityState === "visible"; }, { rootMargin: "200px" });
      io.observe(el);
    }
    raf = requestAnimationFrame(tick);
    return () => {
      cancelAnimationFrame(raf);
      document.removeEventListener("visibilitychange", onVis);
      io?.disconnect();
    };
  }, []);
  return pulse;
}
